/**
 * EntityFriendlyLabel — resolve `(entity_type, entity_id)` to a human-readable
 * identifier so Compliance reviewers don't stare at raw UUIDs on the
 * change-request detail page (Beta #12, 2026-05-26).
 *
 * Mirrors the labelers used backend-side in app/routers/audit.py
 * (_LOOKUP_LABELERS), so the same record displays consistently in
 * audit-log rows and CR detail pages.
 *
 * Resolution per entity_type:
 *   agreements              → code_1                   ("CIIF-002")
 *   persons                 → display_name             ("Bruno Costa Carvalho")
 *   accounts                → bank_name · …last4-IBAN  ("Millennium · …4521")
 *   insurance_policies      → insurer · policy_number  ("Generali · POL-99812")
 *   agreement_documents     → kind · label             ("IAA · 2024-08-18")
 *   agreement_interactions  → Interaction · date · title
 *   agreement_parties /
 *   account_parties /
 *   insurance_policy_parties → ROLE · person.display_name (2-step fetch)
 *   insurance_policy_accounts → policy ↔ account       (2-step fetch)
 *
 * Renders a Skeleton while loading, the friendly label when resolved,
 * or falls back to a short UUID prefix if anything errors out.
 */
import { Skeleton, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { accountsApi } from '../api/accounts';
import { banksApi } from '../api/banks';
import { accountInsuranceApi } from '../api/accountInsurance';
import { accountPartiesApi } from '../api/accountParties';
import { agreementDocumentsApi } from '../api/agreementDocuments';
import { agreementInteractionsApi } from '../api/agreementInteractions';
import { agreementTasksApi } from '../api/agreementTasks';
import { agreementPartiesApi } from '../api/agreementParties';
import { agreementsApi } from '../api/agreements';
import { insurancePolicyPartiesApi } from '../api/insurancePolicyParties';
import { personsApi } from '../api/persons';

interface Props {
  entityType: string;
  entityId: string | null;
  /** When true, also render the raw UUID below the label. */
  showUuid?: boolean;
  /**
   * When supplied (non-null, non-undefined), render this string directly
   * and skip the client-side fetches entirely. Use this prop whenever the
   * parent already has a server-supplied friendly_label (e.g., from a CR
   * list endpoint, audit log row, or any list response that includes
   * pre-resolved labels) — passing it avoids the per-row fan-out of
   * /api/persons/<id>, /api/agreements/<id>, etc. that this component
   * would otherwise produce.
   *
   * Pass null/undefined to fall back to the client-side resolution path
   * (kept for backward compatibility with callers that don't have a
   * pre-resolved label, e.g. when entity_id only appears as a raw UUID).
   *
   * Added 2026-05-28 (Task #231) in response to the DLP rate-gate cascade
   * discovered during the v4 deploy incident.
   */
  preResolvedLabel?: string | null;
}

function ibanTail(iban: string | null | undefined): string {
  if (!iban) return '';
  return iban.slice(-4);
}

function shortUuid(id: string | null | undefined): string {
  return id ? id.slice(0, 8) + '…' : '';
}

export function EntityFriendlyLabel({
  entityType, entityId, showUuid, preResolvedLabel,
}: Props) {
  if (!entityId) {
    // P23 (2026-10-10): a pending document upload has no record yet, but the server
    // labels it from its payload ("Proof of address · on Ana Silva", §3j).
    if (preResolvedLabel) {
      return (
        <Text size="sm" component="span">
          {preResolvedLabel}{' '}
          <Text c="dimmed" size="xs" component="span">(created on approval)</Text>
        </Text>
      );
    }
    return (
      <Text c="dimmed" size="sm" component="span">
        — (CREATE; assigned on approval)
      </Text>
    );
  }

  // Fast path (Task #231): when the parent has already received a
  // server-resolved friendly_label, skip every client-side fetch. The
  // queries below still run their useQuery() hook so React's hook ordering
  // stays stable, but they're disabled — no network traffic, no cascade.
  const hasPreResolved = preResolvedLabel !== undefined && preResolvedLabel !== null;

  const query = useQuery<unknown, Error>({
    queryKey: ['entity-friendly', entityType, entityId],
    queryFn: async () => {
      switch (entityType) {
        case 'agreements':                return agreementsApi.get(entityId);
        case 'persons':                   return personsApi.get(entityId);
        case 'accounts':                  return accountsApi.get(entityId);
        case 'insurance_policies':        return accountInsuranceApi.get(entityId);
        case 'banks':                     return banksApi.get(entityId);   // 0112
        case 'agreement_documents':       return agreementDocumentsApi.get(entityId);
        case 'agreement_interactions':    return agreementInteractionsApi.get(entityId);
        case 'agreement_tasks':           return agreementTasksApi.get(entityId);
        case 'agreement_parties':         return agreementPartiesApi.get(entityId);
        case 'account_parties':           return accountPartiesApi.get(entityId);
        case 'insurance_policy_parties':  return insurancePolicyPartiesApi.get(entityId);
        // Post-0028 (2026-06-05): the `insurance_policy_accounts` join
        // table was collapsed into Account.opened_by_policy_id, so the
        // per-row GET endpoint no longer exists. Old audit_log rows
        // referencing this entity_type still surface here; we resolve
        // to null (the caller falls back to the UUID prefix). Once the
        // historical audit rows age out, the case can be dropped.
        case 'insurance_policy_accounts': return Promise.resolve(null);
        default: return null;
      }
    },
    enabled: !hasPreResolved,
    staleTime: 60_000,
    retry: false,
  });

  const joinRow = query.data as Record<string, unknown> | null | undefined;
  const personIdForJoin =
    entityType === 'agreement_parties' || entityType === 'account_parties' || entityType === 'insurance_policy_parties'
      ? (joinRow?.person_id as string | undefined)
      : undefined;
  const policyIdForIpa =
    entityType === 'insurance_policy_accounts'
      ? (joinRow?.policy_id as string | undefined)
      : undefined;
  const accountIdForIpa =
    entityType === 'insurance_policy_accounts'
      ? (joinRow?.account_id as string | undefined)
      : undefined;

  const personQuery = useQuery<unknown, Error>({
    queryKey: ['persons', 'detail', personIdForJoin ?? ''],
    queryFn: () => personsApi.get(personIdForJoin!),
    enabled: !hasPreResolved && !!personIdForJoin,
    staleTime: 60_000,
    retry: false,
  });
  const policyQuery = useQuery<unknown, Error>({
    queryKey: ['account-insurance', 'detail', policyIdForIpa ?? ''],
    queryFn: () => accountInsuranceApi.get(policyIdForIpa!),
    enabled: !hasPreResolved && !!policyIdForIpa,
    staleTime: 60_000,
    retry: false,
  });
  const accountQuery = useQuery<unknown, Error>({
    queryKey: ['accounts', 'detail', accountIdForIpa ?? ''],
    queryFn: () => accountsApi.get(accountIdForIpa!),
    enabled: !hasPreResolved && !!accountIdForIpa,
    staleTime: 60_000,
    retry: false,
  });

  // Fast-path render: parent supplied the label. Hooks above ran (with
  // enabled=false) so the hook order is consistent on subsequent renders.
  if (hasPreResolved) {
    return (
      <Text size="sm" component="span">
        {preResolvedLabel}
        {showUuid && (
          <Text component="span" c="dimmed" size="xs" ml="xs">
            ({shortUuid(entityId)})
          </Text>
        )}
      </Text>
    );
  }

  if (query.isLoading) return <Skeleton height={16} width={180} />;

  let label = '';
  if (!query.isError && joinRow) {
    switch (entityType) {
      case 'agreements': {
        const a = joinRow as { code_1?: string; client_mnemonic?: string };
        label = a.code_1 && a.client_mnemonic
          ? `${a.code_1} — ${a.client_mnemonic}`
          : (a.code_1 ?? a.client_mnemonic ?? '');
        break;
      }
      case 'persons': {
        const p = joinRow as { display_name?: string };
        label = p.display_name ?? '';
        break;
      }
      case 'accounts': {
        const acc = joinRow as { bank_name?: string; iban?: string };
        const tail = ibanTail(acc.iban);
        label = acc.bank_name && tail
          ? `${acc.bank_name} · …${tail}`
          : (acc.bank_name ?? (tail ? `…${tail}` : ''));
        break;
      }
      case 'banks': {
        const b = joinRow as { mnemonic?: string; name?: string };
        label = [b.mnemonic, b.name].filter(Boolean).join(' · ');
        break;
      }
      case 'insurance_policies': {
        const ip = joinRow as { insurer?: string; policy_number?: string };
        label = ip.insurer && ip.policy_number
          ? `${ip.insurer} · ${ip.policy_number}`
          : (ip.policy_number ?? ip.insurer ?? '');
        break;
      }
      case 'agreement_documents': {
        const d = joinRow as { kind?: string; label?: string; signed_date?: string };
        const date = d.signed_date ?? '';
        label = d.kind && date ? `${d.kind} · ${date}` : (d.kind ?? d.label ?? '');
        break;
      }
      case 'agreement_interactions': {
        const it = joinRow as { interaction_date?: string; title?: string };
        const title = (it.title ?? '').trim();
        const titleSlice = title.length > 50 ? title.slice(0, 50).trimEnd() + '…' : title;
        const date = it.interaction_date ?? '';
        if (date && titleSlice) label = `Interaction · ${date} · ${titleSlice}`;
        else if (date) label = `Interaction · ${date}`;
        else label = titleSlice || '';
        break;
      }
      case 'agreement_tasks': {
        const t = joinRow as { deadline?: string; description?: string };
        const desc = (t.description ?? '').trim();
        const descSlice = desc.length > 50 ? desc.slice(0, 50).trimEnd() + '…' : desc;
        const dl = t.deadline ?? '';
        if (dl && descSlice) label = `Task · ${dl} · ${descSlice}`;
        else if (dl) label = `Task · ${dl}`;
        else label = descSlice || '';
        break;
      }
      case 'agreement_parties':
      case 'account_parties':
      case 'insurance_policy_parties': {
        const jr = joinRow as { role?: string };
        const person = personQuery.data as { display_name?: string } | undefined;
        const personPart = person?.display_name ?? (personIdForJoin ? shortUuid(personIdForJoin) : '');
        label = jr.role ? `${jr.role} · ${personPart}` : personPart;
        break;
      }
      case 'insurance_policy_accounts': {
        const pol = policyQuery.data as { insurer?: string; policy_number?: string } | undefined;
        const acc = accountQuery.data as { bank_name?: string; iban?: string } | undefined;
        const polLabel = pol?.insurer && pol.policy_number
          ? `${pol.insurer} · ${pol.policy_number}`
          : (policyIdForIpa ? shortUuid(policyIdForIpa) : '');
        const accLabel = acc?.bank_name && acc.iban
          ? `${acc.bank_name} · …${ibanTail(acc.iban)}`
          : (accountIdForIpa ? shortUuid(accountIdForIpa) : '');
        label = `${polLabel} ↔ ${accLabel}`;
        break;
      }
    }
  }

  if (!label) {
    return (
      <Text size="sm" component="span">
        <Text component="span" c="dimmed">unresolved · </Text>
        {shortUuid(entityId)}
      </Text>
    );
  }

  return (
    <Text size="sm" component="span">
      {label}
      {showUuid && (
        <Text component="span" c="dimmed" size="xs" ml="xs">
          ({shortUuid(entityId)})
        </Text>
      )}
    </Text>
  );
}
